import React, { forwardRef, useEffect, useState } from "react";
import { Input, Text, Icon, Pressable, Box, useColorMode } from "native-base";
import { Dimensions } from "react-native";
import { Entypo, MaterialIcons } from "@expo/vector-icons";
import { RFPercentage } from "react-native-responsive-fontsize";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import Utils from "../../Utils";

interface InputProps {
  color?: string;
  icon?: string;
  value: string;
  helperText?: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  type?: string;
  width?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  label?: string;
  disabled?: boolean;
  styleBox?: any;
  colorItem?: any;
  corBordaFiltro?: any;
  InputRightElementProps?: any;
  ref?: any;
  bg?: string;
  mask?: string | ((raw: string) => string);
  onChangeRaw?: (raw: string) => void;
  // Visual do layout novo (só aparência; máscara e eventos continuam iguais).
  // Sem essa prop o componente renderiza exatamente como antes.
  estiloNovo?: {
    rotulo?: string;
    corFoco?: string;
    erro?: boolean;
  };
}

const input = forwardRef<any, InputProps>(
  (
    {
      colorItem,
      corBordaFiltro,
      icon,
      value,
      onChangeText,
      placeholder,
      type,
      width,
      onBlur,
      onFocus,
      label,
      helperText,
      disabled,
      styleBox,
      InputRightElementProps,
      bg,
      mask,
      onChangeRaw,
      estiloNovo,
    },
    ref
  ) => {
    const [show, setShow] = useState(false);

    const { colorMode } = useColorMode();

    function handleChangeText(next: string) {
      if (mask) {
        const raw = Utils.unmask(next);
        const maskString =
          typeof mask === "function" ? mask(raw) : (mask as string);
        const formatted = Utils.formatByMask(raw, maskString);
        onChangeText(formatted);
        onChangeRaw?.(raw);
      } else {
        onChangeText(next);
      }
    }

    //vamos aplicar a mascara quando montar o componente, validando se tem mask e se o valor é diferente do valor formatado
    useEffect(() => {
      if (mask && value !== Utils.formatByMask(value, mask as string)) {
        handleChangeText(value);
      }
    }, [mask, value]);

    const keyboardType =
      type === "number" || !!mask ? "numeric" : ("default" as const);

    if (estiloNovo) {
      const corBorda = estiloNovo.erro ? "#C0392B" : "rgba(0,0,0,0.08)";
      const corFoco = estiloNovo.erro
        ? "#C0392B"
        : estiloNovo.corFoco || "#1E7B3C";

      return (
        <Box width={width ? width : "100%"} style={styleBox}>
          {estiloNovo.rotulo && (
            <Text fontSize={13} fontWeight="700" color="#1d1d1d" mb={2}>
              {estiloNovo.rotulo}
            </Text>
          )}
          <Input
            width={"100%"}
            height={"52px"}
            isDisabled={disabled}
            borderRadius={14}
            borderWidth={1}
            borderColor={corBorda}
            color="#1d1d1d"
            fontSize={16}
            bg="#fff"
            _focus={{ borderColor: corFoco, bg: "#fff" }}
            _hover={{ borderColor: corFoco }}
            _disabled={{ bg: "#F7F4EE", opacity: 1 }}
            keyboardType={keyboardType}
            value={value ? value.toString() : value}
            type={type == "password" ? (show ? "text" : "password") : "text"}
            onChangeText={(value) => handleChangeText(value)}
            onFocus={() => {
              if (onFocus) {
                onFocus();
              }
            }}
            InputLeftElement={
              icon && (
                <Icon
                  as={<MaterialIcons name={icon} />}
                  size={5}
                  ml="3"
                  color={estiloNovo.erro ? "#C0392B" : "#9a958c"}
                />
              )
            }
            onBlur={() => {
              if (onBlur) {
                onBlur();
              }
            }}
            InputRightElement={
              (type == "password" && (
                <Pressable onPress={() => setShow(!show)}>
                  <Icon
                    as={
                      <MaterialIcons
                        name={show ? "visibility" : "visibility-off"}
                      />
                    }
                    size={5}
                    mr="3"
                    color="#9a958c"
                  />
                </Pressable>
              )) ||
              InputRightElementProps
            }
            placeholderTextColor="#b5b0a6"
            placeholder={placeholder}
            ref={ref}
          />
          {helperText && (
            <Text
              fontSize={12}
              mt={1.5}
              color={estiloNovo.erro ? "#C0392B" : "#7c7c7c"}
            >
              {helperText}
            </Text>
          )}
        </Box>
      );
    }

    return (
      <Box width={width ? width : wp("100%")} style={styleBox}>
        {label && (
          <Text
            bold
            fontSize={RFPercentage(2.5)}
            color={colorItem ? colorItem : "muted.700"}
          >
            {label}
          </Text>
        )}
        <Input
          width={"100%"}
          isDisabled={disabled}
          borderRadius={5}
          borderColor={corBordaFiltro ? corBordaFiltro : "muted.700"}
          color={colorItem ? colorItem : "muted.700"}
          size="2xl"
          bg={bg ? bg : "transparent"}
          keyboardType={keyboardType}
          value={value ? value.toString() : value}
          type={type == "password" ? (show ? "text" : "password") : "text"}
          onChangeText={(value) => handleChangeText(value)}
          onFocus={() => {
            if (onFocus) {
              onFocus();
            }
          }}
          InputLeftElement={
            icon && (
              <Icon
                as={<MaterialIcons name={icon} />}
                size={5}
                ml="2"
                color={colorItem ? colorItem : "muted.400"}
              />
            )
          }
          onBlur={() => {
            if (onBlur) {
              onBlur();
            }
          }}
          InputRightElement={
            (type == "password" && (
              <Pressable onPress={() => setShow(!show)}>
                <Icon
                  as={
                    <MaterialIcons
                      name={show ? "visibility" : "visibility-off"}
                    />
                  }
                  size={5}
                  mr="2"
                  color={colorItem ? colorItem : "muted.400"}
                />
              </Pressable>
            )) ||
            InputRightElementProps
          }
          placeholderTextColor={colorItem ? colorItem : "muted.400"}
          placeholder={placeholder}
          ref={ref}
        />
        {helperText && (
          <Text fontSize={"sm"} color={colorItem ? colorItem : "muted.400"}>
            {helperText}
          </Text>
        )}
      </Box>
    );
  }
);

export default input;
